/**
 * Cookie notice
 *
 * Shown once, on first visit; "Got it" stores `cookie-consent` in
 * localStorage and hides it for good.
 *
 * It is a NOTICE, not a choice. The site sets no optional cookies, so a
 * "Decline" button would have switched nothing off — and the old copy promised
 * "optional analytics cookies" the app has never loaded. What it does load
 * before any answer is Facebook's SDK (main.tsx, for connecting Facebook and
 * Instagram) and Google Fonts (index.html); the copy says so, and /cookies
 * explains it. Gating those behind a real opt-in is a separate decision.
 *
 * While docked at the bottom it publishes its height as --cookie-banner-height
 * so a page can pad its own bottom (`.pb-cookie-banner`) and keep its last
 * action scrollable clear of it; docked at the top, or gone, it publishes 0.
 */

import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { t } from '@/i18n';

const STORAGE_KEY = 'cookie-consent';
export const BANNER_HEIGHT_VAR = '--cookie-banner-height';

/** Mark a page's primary action with this attribute and the banner keeps clear of it. */
const COOKIE_AVOID_ATTR = 'data-cookie-avoid';

/**
 * True when an element marked `data-cookie-avoid` is on screen and lies under
 * where the card would sit docked at the bottom: its own width, and the
 * bottom `bandHeight` pixels of the viewport.
 */
function bottomBannerCoversAvoided(card: DOMRect, bandHeight: number, viewportHeight: number): boolean {
  const bandTop = viewportHeight - bandHeight;
  return Array.from(document.querySelectorAll(`[${COOKIE_AVOID_ATTR}]`)).some((el) => {
    const r = el.getBoundingClientRect();
    const onScreen = r.width !== 0 && r.height !== 0;
    const sideBySide = Math.max(r.left, card.left) >= Math.min(r.right, card.right);
    return onScreen && !sideBySide && Math.max(r.top, bandTop) < Math.min(r.bottom, viewportHeight);
  });
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [atTop, setAtTop] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // Small delay so the banner doesn't flash during page load
      const timer = setTimeout(() => setVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  // On short screens (375×667) the bottom card sat over the landing hero's
  // "Get started free" — the one button a first-time visitor came for. Dock at
  // the top instead whenever the bottom strip would cover a marked action.
  useLayoutEffect(() => {
    if (!visible) return;
    const place = () => {
      const wrapper = wrapperRef.current;
      const card = cardRef.current;
      if (!wrapper || !card) return;
      const top = bottomBannerCoversAvoided(card.getBoundingClientRect(), wrapper.offsetHeight, window.innerHeight);
      setAtTop(top);
      document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, top ? '0px' : `${wrapper.offsetHeight}px`);
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, { passive: true });
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place);
      document.documentElement.style.removeProperty(BANNER_HEIGHT_VAR);
    };
  }, [visible]);

  // 'accepted' is the value every e2e init script and older browser already holds.
  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'accepted');
    setVisible(false);
  };

  if (!visible) return null;

  // The wrapper is a transparent full-width strip; only the card may take
  // clicks. Without pointer-events-none the strip swallowed clicks on whatever
  // sat beneath it — the OAuth consent Approve button at 1440×789, label
  // visible, no request sent.
  return (
    <div
      ref={wrapperRef}
      role="dialog"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
      data-placement={atTop ? 'top' : 'bottom'}
      className={`fixed ${atTop ? 'top-0' : 'bottom-0'} inset-x-0 z-[9999] p-4 sm:p-6 pointer-events-none`}
    >
      <div ref={cardRef} className="pointer-events-auto mx-auto max-w-3xl bg-white border border-gray-200 rounded-xl shadow-lg p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex-1 text-sm text-gray-600 leading-relaxed">
            <p>
              {t('cookieNotice.body')}{' '}
              <a href="/cookies" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-500 underline">
                {t('legal.cookiesLink')}
              </a>
            </p>
          </div>

          <div className="flex items-center gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={handleDismiss}
              className="rounded-full px-4 py-2 text-sm font-medium text-white bg-gray-900 transition-colors hover:bg-gray-800"
            >
              {t('cookieNotice.ok')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
